import OlStyle from 'ol/style/Style';
import OlIcon from 'ol/style/Icon';
import OlFill from 'ol/style/Fill';
import OlStroke from 'ol/style/Stroke';
import OlText from 'ol/style/Text';
import Circle from 'ol/geom/Circle';

import { NEDGLOBE_VIEWER } from '../../../constants';
import { ICON_UNITS } from '../../../constants/api';
import { isSvgBase64Image, numberSortFunction } from '../../../services/utilities';

export function getStyle(currentStyle, feature, resolution, zIndex = 800, feedbackFields = null) {
    // Set elements
    const iconScale = currentStyle.iconSizeUnits === ICON_UNITS.meters ? (1.0 / resolution) : 1.0;
    const icon = GetIcon(currentStyle[NEDGLOBE_VIEWER.styleOptionNames.icon], currentStyle[NEDGLOBE_VIEWER.styleOptionNames.iconSize], iconScale);

    const featureType = feature.getGeometry().getType();

    const stroke = GetStrokeByFeatureType(featureType, currentStyle);
    const fill = GetFillOrTransparent(feature, stroke, currentStyle);

    let circle;
    if (featureType === 'Point' || featureType === 'MultiPoint') {
        circle = GetCircle(
            currentStyle[NEDGLOBE_VIEWER.styleOptionNames.pointSize],
            icon,
            feature,
            resolution
        );
    }

    if (!icon && !circle && !stroke && !fill) {
        return null;
    }

    // Get text
    const text = createTextStyle(currentStyle, feature, featureType, fill, stroke, resolution, feedbackFields);

    const retval = [];
    if (circle) {
        // style points as circle geometry (and not as an image) to avoid decluttering
        retval.push(new OlStyle({
            geometry: circle,
            fill,
            stroke,
            text,
            zIndex
        }));
    }
    if (icon) {
        retval.push(new OlStyle({
            image: icon,
            fill,
            stroke,
            text,
            zIndex
        }));
    }
    if (!icon && !circle) {
        retval.push(new OlStyle({
            fill,
            stroke,
            text,
            zIndex
        }));
    }
    return retval;
}

function DetermineTextValue(feature, featureType, showLabel, labels, textAttribute, feedbackFields) {
    // feedback layers do not have a predefined label attribute.
    if (showLabel) {
        return GetFeedbackLabel(feature, featureType);
        // Use new InformationAttribute stored labels
    } else if (labels && labels.length > 0) {
        return GetTextValue(feature, labels, feedbackFields);
        // Use style stored textAttribte
    } else if (textAttribute) {
        return GetTextAttributeValue(feature, textAttribute);
    }
    return null;
}

function GetTextAttributeValue(feature, textAttribute) {
    const properties = feature.getProperties();
    return properties[textAttribute];
}

// Get OpenLayers Text value
export function GetTextValue(feature, labels, feedbackFields) {
    const properties = feature.getProperties();
    let textValue;

    labels.sort(numberSortFunction('textAttributeOrder')).forEach((labelAttribute) => {
        let attrValue = '';

        // replace ln with \n to insert a newline
        // otherwise sep 2 characters long ending in a space.
        const separator = labelAttribute.textAttributeSeparator ?? ',';
        let sep = '';
        if (separator === 'lf') {
            sep = '\n';
        } else {
            sep = separator.padEnd(2, ' ');
        }
        let label = labelAttribute.alias;
        if (labelAttribute.textAttributeLabel && labelAttribute.textAttributeLabel !== '') {
            label = labelAttribute.textAttributeLabel;
        }
        // If we want a label add a label to this attributes value.
        // For feedbacks properties has attributenames
        let newValue = properties[labelAttribute.id.toString()] ?? properties[labelAttribute.attribute];
        if (feedbackFields) {
            let domainValues = feedbackFields.find(fbf => fbf.alias === labelAttribute.alias).domain;
            if (domainValues && domainValues.keyvalues !== null) {
                // If this label is a domain, then show domainValue, not domainKey
                const domainValue = domainValues.keyvalues.find((kv) => {
                    return kv.key === newValue;
                });
                if (domainValue) {
                    newValue = domainValue.value;
                }
            }
        }

        if (labelAttribute.textAttributeShowLabel) {
            attrValue = attrValue.concat(label, ': ', newValue);
        } else {
            attrValue = newValue;
        }
        attrValue = attrValue ?? '';
        if (textValue) {
            textValue = textValue.concat(sep, attrValue.toString());
        } else {
            textValue = attrValue.toString();
        }
    });
    return textValue;
}
// Get OpenLayers Text value
function GetFeedbackLabel(feature, featureType) {
    const properties = feature.getProperties();
    if (properties['length'] || properties['area'] || properties['coord_x']) {
        if (featureType === 'MultiLineString' || featureType === 'LineString') {
            return `Afstand: \xB1${properties['length']}m`;
        } else if (featureType === 'Polygon') {
            return `Oppervlak: \xB1${properties['area']}m\xB2\nOmtrek: \xB1${properties['length']}m`;
        } else if (featureType === 'Point') {
            return `X: ${properties['coord_x']}\nY: ${properties['coord_y']}`;
        }
        return undefined;
    }
}

function GetRotationValue(feature, featureType, rotationAttribute) {
    const properties = feature.getProperties();
    const rotation = (featureType === NEDGLOBE_VIEWER.styledObjectTypes.Point && properties[rotationAttribute]) ? properties[rotationAttribute] : 0;
    const pi = Math.PI;
    return rotation * (pi / 180);
}

function GetAlignmentValue(featureType, fillColor, strokeColor) {
    if (fillColor && typeof fillColor.getColor() === 'string') {
        const fillTransparencyValue = fillColor.getColor()?.split(',')[3]?.replace(')', '')?.trim();
        const strokeTransparencyValue = strokeColor.getColor()?.split(',')[3]?.replace(')', '')?.trim();
        if (featureType === NEDGLOBE_VIEWER.styledObjectTypes.Point && fillTransparencyValue === '0' && strokeTransparencyValue === '0') {
            return 'center';
        }
    }
    return (featureType === NEDGLOBE_VIEWER.styledObjectTypes.Point) ? 'left' : 'center';
}

function GetTextPlacementValue(featureType) {
    if (featureType === 'MultiLineString' || featureType === 'LineString') {
        return 'line';
    }
    return 'point';
}

function GetMaxAngleValue(featureType) {
    return (featureType === 'MultiLineString' || featureType === 'LineString') ? Infinity : undefined;
}

// Get OpenLayers Text object
function createTextStyle(currentStyle, feature, featureType, fill, stroke, resolution, feedbackFields = null) {
    const maxResolution = currentStyle[NEDGLOBE_VIEWER.styleOptionNames.textMaxResolution];
    const isInRange = maxResolution ? resolution < maxResolution : true;

    const textAttribute = currentStyle[NEDGLOBE_VIEWER.styleOptionNames.textAttribute];
    const textValue = DetermineTextValue(feature, featureType, currentStyle.showLabel, currentStyle.labels, textAttribute, feedbackFields);
    const rotationAttribute = currentStyle[NEDGLOBE_VIEWER.styleOptionNames.rotationAttribute];
    const rotationValue = GetRotationValue(feature, featureType, rotationAttribute);
    const alignmentValue = GetAlignmentValue(featureType, fill, stroke);

    const font = currentStyle[NEDGLOBE_VIEWER.styleOptionNames.textFont];
    const fontSize = currentStyle[NEDGLOBE_VIEWER.styleOptionNames.textFontSize];
    const strokeColor = currentStyle[NEDGLOBE_VIEWER.styleOptionNames.textStrokeColor];
    const strokeWidth = currentStyle[NEDGLOBE_VIEWER.styleOptionNames.textStrokeWidth];
    const fillColor = currentStyle[NEDGLOBE_VIEWER.styleOptionNames.textFillColor];

    let olText;
    if (textValue && isInRange) {
        olText = new OlText({
            font: font ? `bold ${fontSize}px ${font}` : 'bold 11px/1.5 Arial',
            text: textValue ? textValue.toString() : null, // Make sure label text is a string
            overflow: true,
            fill: fillColor && new OlFill({ color: fillColor }),
            stroke: strokeColor ? new OlStroke({ color: strokeColor, width: strokeWidth }) : new OlStroke({ color: '#fff', width: 2 }),
            offsetX: alignmentValue === null ? 18 : 0,
            textAlign: alignmentValue === null ? undefined : alignmentValue,
            textBaseline: alignmentValue === null ? undefined : 'bottom',
            rotation: rotationValue,
            placement: GetTextPlacementValue(featureType),
            maxAngle: GetMaxAngleValue(featureType)
        });
    }
    return olText;
}

/**
* Creates a cluster style
* This shows an aggregated view of objects while zoomed out
* This is used for Web-feedback layers
* @return {OlStyle} the style of the Cluster layer
*/
export function getClusterStyle(feature, resolution, clusterStyle, numberOfFeatures, publicEndPoint) {
    // Initialize with text
    const olClusterStyle = new OlStyle({
        text: new OlText({
            text: numberOfFeatures.toString(),
            fill: new OlFill({ color: '#fff' })
        })
    });
    // Use point image if possible (icon contains base64 encoded string, pointImage is reference to a file)
    if (clusterStyle.pointImage || clusterStyle.icon) {
        const image = new OlIcon({
            anchor: [0.5, 0.5],
            crossOrigin: 'anonymous',
            src: clusterStyle.icon ? clusterStyle.icon : `${publicEndPoint}/file/getsldimage/${clusterStyle.pointImage}`
        });
        const iconSize = clusterStyle.iconSize;
        if (clusterStyle.icon && iconSize && isSvgBase64Image(clusterStyle.icon)) {
            const iconScale = clusterStyle.iconSizeUnits === ICON_UNITS.meters ? (1.0 / resolution) : 1.0;
            image.setScale([
                ((iconSize) / image.getImage().width) * iconScale,
                ((iconSize) / image.getImage().height) * iconScale,
            ]);
        }
        olClusterStyle.setImage(image);
    } else {
        // Otherwise use circle
        olClusterStyle.setGeometry(new Circle(feature.getGeometry().getCoordinates(), clusterStyle.pointSize * resolution));
        olClusterStyle.setFill(new OlFill({ color: clusterStyle.fillColor }));
        olClusterStyle.setStroke(new OlStroke({
            color: clusterStyle.strokeColor,
            width: clusterStyle.strokeWidth
        }));
    }
    return olClusterStyle;
}

// Get OpenLayers Circle object
export function GetCircle(radius, icon, feature, resolution) {
    // When an icon is set, do not show a circle
    if (icon) {
        return null;
    } else if (radius) {
        // GetFirstCoordinate works for Point and Multipoint
        return new Circle(feature.getGeometry().getFirstCoordinate(), radius * resolution);
    }
    return null;
}

// Get OpenLayers Icon object
export function GetIcon(src, iconSize, iconScale) {
    if (src) {
        const icon = new OlIcon({ src });
        if (iconSize && isSvgBase64Image(src)) {
            icon.setScale([
                ((iconSize) / icon.getImage().width) * iconScale,
                ((iconSize) / icon.getImage().height) * iconScale,
            ]);
        }
        return icon;
    }
    return null;
}

function setFillPattern(feature, settings) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    let tempCanvas = document.createElement('canvas');
    let tempContext = tempCanvas.getContext('2d');
    const size = settings.size;

    tempCanvas.width = size;
    tempCanvas.height = size;

    let img = new Image();
    img.onload = function () {
        tempContext.rect(0, 0, size, size);
        tempContext.fillStyle = settings.fillColor;
        tempContext.fill();

        tempContext.drawImage(img, 0, 0, size, size);
        feature.setStyle(new OlStyle({
            fill: new OlFill({ color: context.createPattern(tempCanvas, 'repeat') }),
            stroke: settings.stroke
        }));
    };
    img.src = settings.source;
}

// Get OpenLayers Fill object
function GetFill(color) {
    if (color) {
        return new OlFill({ color });
    }
    return null;
}

function GetFillOrTransparent(feature, stroke, currentStyle) {
    // Return 'transparent' to not show any fill
    if (currentStyle.hideFill) {
        return GetFill('transparent');
    } else if (currentStyle[NEDGLOBE_VIEWER.styleOptionNames.imageSvg]) {
        setFillPattern(
            feature,
            {
                source: currentStyle[NEDGLOBE_VIEWER.styleOptionNames.imageSvg],
                size: currentStyle[NEDGLOBE_VIEWER.styleOptionNames.sizeSvg],
                fillColor: currentStyle[NEDGLOBE_VIEWER.styleOptionNames.fillColor],
                stroke
            });
        return null;
    } else if (currentStyle[NEDGLOBE_VIEWER.styleOptionNames.fillColor]) {
        return GetFill(currentStyle[NEDGLOBE_VIEWER.styleOptionNames.fillColor]);
    }
}

// Get OpenLayers Stroke object
export function GetStrokeByFeatureType(featureType, currentStyle) {
    let stroke;
    switch (featureType) {
        case 'MultiLineString': // FallThrough
        case 'LineString':
            stroke = GetStroke(
                currentStyle[NEDGLOBE_VIEWER.styleOptionNames.fillColor],
                currentStyle[NEDGLOBE_VIEWER.styleOptionNames.strokeWidth]
            );
            break;
        default:
            stroke = GetStroke(
                currentStyle[NEDGLOBE_VIEWER.styleOptionNames.strokeColor],
                currentStyle[NEDGLOBE_VIEWER.styleOptionNames.strokeWidth]
            );
            break;
    }

    if (currentStyle.hideFill) {
        // If no stroke available generate get from settings or generate a default stroke
        if (stroke && stroke.getColor() === 'transparent') {
            if (currentStyle.strokeColorNoFill) {
                stroke = GetStroke(currentStyle.strokeColorNoFill, currentStyle.strokeWidthNoFill);
            } else {
                stroke = GetStroke(
                    'rgba(0,0,255,1)',
                    1
                );
            }
        }
    }

    return stroke;
}

export function GetStroke(color, width) {
    if (width === null || !color) {
        return null;
    }
    return new OlStroke({ color, width });
}
